import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { BehaviorSubject, Observable, of, from, throwError, timeout } from 'rxjs';
import { filter, switchMap, take, tap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class TokenExchangeService {
  private proxyToken: string | null = null;
  private proxyTokenExpiresAt: number | null = null;
  private tokenReady$ = new BehaviorSubject<boolean>(false);
  private tokenExchangeInProgress = false;
  private tokenExchangeFailed = false;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly refreshLeadTimeMs = 5 * 60 * 1000;
  private readonly fallbackRefreshIntervalMs = 10 * 60 * 1000;

  constructor(private http: HttpClient, private msalService: MsalService) {}

  /**
   * Exchanges the MSAL token for a proxy token by calling the proxy /api/Token endpoint.
   * Returns an observable that emits the proxy token.
   */
  exchangeToken(forceRefresh = false): Observable<string> {
    if (
      !forceRefresh &&
      this.proxyToken &&
      this.tokenReady$.getValue() &&
      !this.isTokenNearExpiry()
    ) {
      return of(this.proxyToken);
    }

    if (this.tokenExchangeInProgress) {
      return this.tokenReady$.pipe(
        filter((ready) => ready),
        take(1),
        switchMap(() => of(this.proxyToken!))
      );
    }

    this.tokenExchangeInProgress = true;

    return this.acquireMsalToken().pipe(
      switchMap((msalToken) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${msalToken}`,
          'Content-Type': 'application/json',
        });

        const body = {
          scope: environment.proxyScope,
        };

        return this.http.post<{ token: string }>(
          environment.proxyTokenUrl,
          body,
          { headers, context: skipTokenExchangeContext() }
        );
      }),
      tap((response: any) => {
        this.proxyToken =
          response.token || response.access_token || response.accessToken;
        if (!this.proxyToken) {
          throw new Error('Proxy token response did not include a token value');
        }
        this.proxyTokenExpiresAt = this.getTokenExpiryMs(this.proxyToken);
        this.scheduleRefresh();
        this.tokenExchangeInProgress = false;
        this.tokenExchangeFailed = false;
        this.tokenReady$.next(true);
      }),
      switchMap(() => of(this.proxyToken!)),
      catchError((error) => {
        this.tokenExchangeInProgress = false;
        this.tokenExchangeFailed = true;
        console.error('Token exchange failed:', error);
        throw error;
      })
    );
  }

  /**
   * Returns the cached proxy token, or null if not yet exchanged.
   */
  getProxyToken(): string | null {
    return this.proxyToken;
  }

  /**
   * Returns a token suitable for API calls, refreshing first when expiry is near.
   */
  getValidProxyToken(): Observable<string> {
    if (this.proxyToken && !this.isTokenNearExpiry()) {
      return of(this.proxyToken);
    }

    return this.exchangeToken(true).pipe(
      catchError((error) => {
        if (this.proxyToken) {
          // Keep app usable if proactive refresh fails but current token may still work briefly.
          console.warn('Using existing proxy token after refresh failure:', error);
          return of(this.proxyToken);
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Returns token-state diagnostics without exposing token values.
   */
  getDiagnosticsSnapshot(): {
    hasProxyToken: boolean;
    tokenReady: boolean;
    tokenExchangeInProgress: boolean;
    tokenExchangeFailed: boolean;
    tokenExpiresAtUtc: string | null;
    tokenExpiresInSeconds: number | null;
  } {
    const now = Date.now();
    const expiresInSeconds =
      this.proxyTokenExpiresAt == null
        ? null
        : Math.max(0, Math.floor((this.proxyTokenExpiresAt - now) / 1000));

    return {
      hasProxyToken: !!this.proxyToken,
      tokenReady: this.tokenReady$.getValue(),
      tokenExchangeInProgress: this.tokenExchangeInProgress,
      tokenExchangeFailed: this.tokenExchangeFailed,
      tokenExpiresAtUtc:
        this.proxyTokenExpiresAt == null
          ? null
          : new Date(this.proxyTokenExpiresAt).toISOString(),
      tokenExpiresInSeconds: expiresInSeconds,
    };
  }

  /**
   * Clears the cached proxy token (e.g., on logout or token expiry).
   */
  clearProxyToken(): void {
    this.proxyToken = null;
    this.proxyTokenExpiresAt = null;
    this.clearRefreshTimer();
    this.tokenReady$.next(false);
    this.tokenExchangeFailed = false;
  }

  /**
   * Returns an observable that emits once the proxy token is available.
   */
  waitForToken(): Observable<string> {
    if (this.proxyToken && !this.isTokenNearExpiry()) {
      return of(this.proxyToken);
    }
    if (this.proxyToken) {
      return this.exchangeToken(true).pipe(
        catchError((error) => {
          console.warn('Returning existing proxy token after refresh failure:', error);
          return of(this.proxyToken!);
        })
      );
    }
    // If a previous exchange failed, don't retry here — let the caller decide
    if (this.tokenExchangeFailed) {
      return throwError(() => new Error('Token exchange previously failed'));
    }
    // Wait for token with 10 second timeout (enough time for first exchange)
    return this.tokenReady$.pipe(
      filter((ready) => ready),
      take(1),
      timeout(10000),
      switchMap(() => of(this.proxyToken!)),
      catchError((err) => {
        console.error('Timeout waiting for token:', err);
        return throwError(() => new Error('Timeout waiting for proxy token (10s)'));
      })
    );
  }

  /**
   * Acquires a MSAL access token silently using the active account.
   */
  private acquireMsalToken(): Observable<string> {
    let account = this.msalService.instance.getActiveAccount();
    if (!account) {
      const firstAvailableAccount = this.msalService.instance.getAllAccounts()[0];
      if (firstAvailableAccount) {
        this.msalService.instance.setActiveAccount(firstAvailableAccount);
        account = firstAvailableAccount;
      }
    }

    if (!account) {
      // If no active account yet (MSAL still initializing), fail gracefully
      console.warn('No active MSAL account for token exchange — user may not be authenticated yet');
      return throwError(() => new Error('No active MSAL account'));
    }

    const scopes = environment.authConfig.addedProtectedResourceMap.reduce(
      (acc: string[], entry: any) => acc.concat(entry.list),
      []
    );

    return from(
      this.msalService.instance.acquireTokenSilent({
        account,
        scopes,
      })
    ).pipe(
      switchMap((result) => of(result.accessToken)),
      catchError((err) => {
        console.error('Failed to acquire MSAL token:', err);
        return throwError(() => err);
      })
    );
  }

  private isTokenNearExpiry(): boolean {
    if (!this.proxyToken) {
      return true;
    }

    if (this.proxyTokenExpiresAt == null) {
      return false;
    }

    return Date.now() >= this.proxyTokenExpiresAt - this.refreshLeadTimeMs;
  }

  private getTokenExpiryMs(token: string): number | null {
    try {
      const parts = token.split('.');
      if (parts.length < 2) {
        return null;
      }

      const payload = this.decodeBase64Url(parts[1]);
      const parsed = JSON.parse(payload);
      if (!parsed || typeof parsed.exp !== 'number') {
        return null;
      }

      return parsed.exp * 1000;
    } catch (error) {
      console.warn('Unable to parse proxy token expiry:', error);
      return null;
    }
  }

  private decodeBase64Url(input: string): string {
    const normalized = input.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    return atob(padded);
  }

  private scheduleRefresh(): void {
    this.clearRefreshTimer();

    if (!this.proxyToken) {
      return;
    }

    const now = Date.now();
    const refreshAt = this.proxyTokenExpiresAt == null
      ? now + this.fallbackRefreshIntervalMs
      : Math.max(now + 5000, this.proxyTokenExpiresAt - this.refreshLeadTimeMs);

    const delay = Math.max(0, refreshAt - now);
    this.refreshTimer = setTimeout(() => {
      this.exchangeToken(true).subscribe({
        next: () => {
          // No-op: success path already updates token and reschedules.
        },
        error: (error) => {
          console.error('Proactive proxy token refresh failed:', error);
          // Retry sooner after failure to avoid waiting until next API call.
          this.clearRefreshTimer();
          this.refreshTimer = setTimeout(() => {
            this.exchangeToken(true).subscribe({
              next: () => {
                // No-op
              },
              error: (retryError) => {
                console.error('Retry proactive proxy token refresh failed:', retryError);
                this.scheduleRefresh();
              },
            });
          }, 30000);
        },
      });
    }, delay);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}

// HttpContext token to mark requests that should skip the proxy token interceptor
import { HttpContext, HttpContextToken } from '@angular/common/http';

export const SKIP_TOKEN_EXCHANGE = new HttpContextToken<boolean>(() => false);

export function skipTokenExchangeContext(): HttpContext {
  return new HttpContext().set(SKIP_TOKEN_EXCHANGE, true);
}
